/**
 * XP system - quantifies the *behavioral impact* of each completed task and
 * splits that impact across three reportable buckets (dimensions, tags,
 * pendulums) on the **same magnitude scale** so the dashboard chips look
 * proportional rather than "+135 vs +5".
 *
 * The math (per intervention `iv`):
 *
 * 1. taskXP - what one completion is "worth":
 *      taskXP = base
 *             + Σ |contributes_to_tags[w]| × weight_multiplier
 *             + cognitive_bonus[load]
 *             + duration_bonus(dosage.duration_minutes)
 *
 * 2. Split into 3 buckets that add up to taskXP:
 *      dimensionXP[d]       = taskXP × shares.dimension × cascaded pattern→dimension strength
 *      tagXP[t]             = taskXP × shares.tag      × |w_t| / Σ|w|
 *      pendulumXP[a]        = taskXP × shares.pendulum × strength_a / Σ strength
 *
 *    `shares.dimension + shares.tag + shares.pendulum` should sum to 1.0 - the
 *    user sees the same currency on every chip and the totals align.
 *
 * 3. Cumulative XP across `completedIds` is just Σ contribution.totalXP. Task
 *    ids are *month-scoped* (`<intervention.id>__m<monthIndex>`) so the same
 *    technique repeated in Months 1, 2, 3 counts as 3 separate completions -
 *    the user has to do it again each month, the way real practice works.
 *    Use `ivIdFromTaskId(taskId)` to map a slot back to its intervention.
 *
 * All multipliers, brackets and shares come from `content.ui.xp` so they are
 * tunable from the content layer, not from code.
 */

import { Intervention, UnifiedContent } from './contentLoader';

export interface InterventionContribution {
  totalXP: number;
  dimensionXP: Record<string, number>;
  /** tagId → xp delta. Keyed only on tags actually touched. */
  tagXP: Record<string, number>;
  /** axisId → xp delta. Keyed only on pendulums actually touched. */
  pendulumXP: Record<string, number>;
}

export interface XPBreakdown {
  totalXP: number;
  level: number;
  /** XP into the current level (always 0..levelStep-1). */
  xpInLevel: number;
  /** XP needed to hit the next level. */
  xpToNextLevel: number;
  dimensionXP: Record<string, number>;
  tagXP: Record<string, number>;
  pendulumXP: Record<string, number>;
  /** Number of completed task slots counted into the breakdown. */
  completedCount: number;
}

const TASK_ID_SEPARATOR = '__m';

/**
 * Build the unique slot id for one (intervention, month) pair. Composite ids
 * make every month an independent completion bucket - finishing a slot in
 * Month 1 does NOT auto-complete the same intervention's slot in Month 2.
 */
export function makeTaskId(interventionId: string, monthIndex: number): string {
  return `${interventionId}${TASK_ID_SEPARATOR}${monthIndex}`;
}

/** Strip the `__m{N}` suffix to get back the original intervention id. */
export function ivIdFromTaskId(taskId: string): string {
  const i = taskId.indexOf(TASK_ID_SEPARATOR);
  return i >= 0 ? taskId.slice(0, i) : taskId;
}

function xpConfig(content: UnifiedContent) {
  return {
    base: content.ui.xp.base,
    weightMult: content.ui.xp.weight_multiplier,
    cognitiveBonus: content.ui.xp.cognitive_bonus,
    durationBrackets: content.ui.xp.duration_brackets,
    shares: content.ui.xp.shares,
    levelStep: content.ui.xp.level_step,
  };
}

function durationBonus(
  minutes: number | null | undefined,
  brackets: Array<{ max_minutes: number | null; bonus: number }>,
): number {
  if (!brackets.length) return 0;
  const m = typeof minutes === 'number' && minutes > 0 ? minutes : 0;
  const hit = brackets.find((b) => b.max_minutes === null || m <= b.max_minutes);
  return hit ? hit.bonus : (brackets[brackets.length - 1]?.bonus ?? 0);
}

function cognitiveBonus(
  load: string | undefined,
  table: Record<'low' | 'medium' | 'high', number>,
): number {
  if (load === 'low' || load === 'medium' || load === 'high') return table[load] ?? 0;
  return table.medium ?? 0;
}

/**
 * Compute the raw XP value of a single intervention (before the
 * dimension/tag/pendulum split). Pulled into its own function so any preview
 * UI can ask "what is THIS task worth?" without re-deriving the formula.
 */
export function computeTaskXP(iv: Intervention, content: UnifiedContent): number {
  const cfg = xpConfig(content);

  let weightSum = 0;
  Object.values(iv.contributes_to_tags || {}).forEach((w) => {
    weightSum += Math.abs(w);
  });

  const xp =
    cfg.base +
    weightSum * cfg.weightMult +
    cognitiveBonus(iv.dosage?.cognitive_load, cfg.cognitiveBonus) +
    durationBonus(iv.dosage?.duration_minutes, cfg.durationBrackets);

  // Round to nearest 5 so XP looks hand-tuned, not like a hash.
  return Math.max(cfg.base, Math.round(xp / 5) * 5);
}

/**
 * All reportable XP/pts values are rounded to whole integers. The point system
 * is meant to feel like a tally - fractional points (e.g. 4.6 pts) read as
 * noise, not progress. Internal math still keeps full precision; only the
 * presentation layer (this rounder + `formatXP`) is integer-only.
 */
function roundInt(v: number): number {
  return Math.round(v);
}

function roundMap(m: Record<string, number>): Record<string, number> {
  const out: Record<string, number> = {};
  Object.entries(m).forEach(([k, v]) => { out[k] = roundInt(v); });
  return out;
}

/**
 * Distribute the per-task XP across the dimension/tag/pendulum buckets so that
 * `dimensionXP + tagXP + pendulumXP ≈ taskXP`. Used by the dashboard to render
 * contribution chips with consistent magnitudes (no more 135 vs 5 chasm).
 */
export function interventionContribution(
  iv: Intervention,
  content: UnifiedContent,
): InterventionContribution {
  const cfg = xpConfig(content);
  const taskXP = computeTaskXP(iv, content);
  const dimensionXP: Record<string, number> = {};
  const tagXP: Record<string, number> = {};
  const pendulumXP: Record<string, number> = {};

  // Pattern contributions are the common source for all three layers.
  const tagEntries = Object.entries(iv.contributes_to_tags || {})
    .map(([id, w]) => ({ id, mag: Math.abs(w) }))
    .filter((t) => t.mag > 0);
  const totalTagMag = tagEntries.reduce((acc, t) => acc + t.mag, 0);

  // 1. Dimension share - cascade every exercise tag through
  // tag.dimension_weights. Fall back to primary_dimension only when an old
  // intervention has no usable tag mapping.
  const dimensionStrength: Record<string, number> = {};
  tagEntries.forEach((t) => {
    const tagMeta = content.tags[t.id];
    Object.entries(tagMeta?.dimension_weights || {}).forEach(([dimId, w]) => {
      dimensionStrength[dimId] = (dimensionStrength[dimId] || 0) + t.mag * Math.abs(w);
    });
  });
  const totalDimensionStrength = Object.values(dimensionStrength).reduce((a, b) => a + b, 0);
  if (totalDimensionStrength > 0) {
    const dimensionBudget = taskXP * cfg.shares.dimension;
    Object.entries(dimensionStrength).forEach(([dimId, strength]) => {
      dimensionXP[dimId] = (strength / totalDimensionStrength) * dimensionBudget;
    });
  } else if (iv.primary_dimension) {
    dimensionXP[iv.primary_dimension] = taskXP * cfg.shares.dimension;
  }

  // 2. Tag share - split proportionally to |contribution weight|.
  if (totalTagMag > 0) {
    const tagBudget = taskXP * cfg.shares.tag;
    tagEntries.forEach((t) => {
      tagXP[t.id] = (t.mag / totalTagMag) * tagBudget;
    });
  }

  // 3. Pendulum share - cascade tag weights through tag.pendulum_weights, then
  //    split the pendulum budget proportionally to each axis's total strength.
  const axisStrength: Record<string, number> = {};
  tagEntries.forEach((t) => {
    const tagMeta = content.tags[t.id];
    if (!tagMeta) return;
    Object.entries(tagMeta.pendulum_weights || {}).forEach(([axisId, poleMap]) => {
      // A pendulum is mirrored: mapping a pattern to either pole activates the
      // whole axis. Use the strongest pole weight, not the sum of both poles.
      const axisWeight = Math.max(0, ...Object.values(poleMap).map(Math.abs));
      if (axisWeight > 0) {
        axisStrength[axisId] = (axisStrength[axisId] || 0) + t.mag * axisWeight;
      }
    });
  });
  const totalAxisStrength = Object.values(axisStrength).reduce((a, b) => a + b, 0);
  if (totalAxisStrength > 0) {
    const pendBudget = taskXP * cfg.shares.pendulum;
    Object.entries(axisStrength).forEach(([axisId, str]) => {
      pendulumXP[axisId] = (str / totalAxisStrength) * pendBudget;
    });
  }

  return {
    totalXP: taskXP,
    dimensionXP: roundMap(dimensionXP),
    tagXP: roundMap(tagXP),
    pendulumXP: roundMap(pendulumXP),
  };
}

/** Sum two contribution maps in-place - used to build the cumulative breakdown. */
function addInto(target: Record<string, number>, src: Record<string, number>): void {
  Object.entries(src).forEach(([k, v]) => {
    target[k] = (target[k] || 0) + v;
  });
}

/**
 * Cumulative XP earned by `completedIds`. Slot ids are month-scoped, so the
 * same intervention completed in 3 different months counts 3 times - that's
 * the whole point of the cyclic roadmap. We strip the `__m{N}` suffix to look
 * up the underlying intervention; orphans (ids that no longer resolve) are
 * silently skipped.
 */
export function computeXP(
  completedIds: Set<string>,
  content: UnifiedContent,
): XPBreakdown {
  const cfg = xpConfig(content);
  const dimensionXP: Record<string, number> = {};
  const tagXP: Record<string, number> = {};
  const pendulumXP: Record<string, number> = {};
  let totalXP = 0;
  let counted = 0;

  completedIds.forEach((slotId) => {
    const iv = content.interventions[ivIdFromTaskId(slotId)];
    if (!iv) return;
    counted += 1;
    const contrib = interventionContribution(iv, content);
    totalXP += contrib.totalXP;
    addInto(dimensionXP, contrib.dimensionXP);
    addInto(tagXP, contrib.tagXP);
    addInto(pendulumXP, contrib.pendulumXP);
  });

  const step = Math.max(1, cfg.levelStep);
  const level = Math.floor(totalXP / step) + 1;
  const xpInLevel = totalXP - (level - 1) * step;
  const xpToNextLevel = step - xpInLevel;

  return {
    totalXP: roundInt(totalXP),
    level,
    xpInLevel: roundInt(xpInLevel),
    xpToNextLevel: roundInt(xpToNextLevel),
    dimensionXP: roundMap(dimensionXP),
    tagXP: roundMap(tagXP),
    pendulumXP: roundMap(pendulumXP),
    completedCount: counted,
  };
}

/**
 * Format an XP/points value for display.
 *
 *   compact:false  → "3,300", "240", "8" (full integer with thousands separator).
 *                    Used wherever the metric is a hero number with breathing room.
 *   compact:true   → "3.3K", "240", "8" (no fractions for sub-thousand, K-suffix
 *                    above 1k). Used in inline chips / tooltips where space is
 *                    tight. The `K` is always uppercase to match the headline
 *                    typography.
 */
export function formatXP(xp: number, opts: { compact?: boolean } = {}): string {
  const n = Math.round(xp);
  if (opts.compact) {
    if (n >= 10000) return `${Math.round(n / 1000)}K`;
    if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, '')}K`;
    return n.toLocaleString('en-US');
  }
  return n.toLocaleString('en-US');
}
